import React, { useCallback, useEffect, useMemo, useState } from "react"

import {
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  SearchInput,
  SelectionChip,
  Table,
  type ColumnDef,
  type SortDescriptor,
} from "@coval/ui"

interface EntityPickerDialogProps<T> {
  isOpen: boolean
  onCancel: () => void
  onApply: () => void
  applyDisabled?: boolean
  /** Dialog heading, e.g. "Select Agents". The live count is appended. */
  title: string
  items: T[]
  columns: ColumnDef<T>[]
  getItemId: (item: T) => string
  /** Chip label for a selected row. */
  getItemLabel: (item: T) => string
  /** Free-text match against one row, given a lowercased query. */
  matchesSearch: (item: T, query: string) => boolean
  /** The draft selection the dialog edits. */
  selectedIds: string[]
  onSelectionChange: (ids: string[]) => void
  isLoading?: boolean
  /** `false` keeps the draft at a single row: picking one replaces the last. */
  allowMultiple?: boolean
  /**
   * Marks a row that can be selected in principle but not against the current
   * draft. Such a row keeps a disabled checkbox rather than losing it, so the
   * rule reads as a rule instead of as a missing row.
   */
  isSelectionDisabled?: (item: T) => boolean
  /**
   * Last word on what a selection may contain, applied to every change before
   * it is emitted. `isSelectionDisabled` gates one row against the draft as it
   * stands, which the table's select-all steps around by adding every row in a
   * single update — nothing is blocked yet at the moment it is evaluated. A
   * caller whose rows constrain each other reconciles the whole set here.
   */
  reconcileSelection?: (ids: string[]) => string[]
  onSortChange?: (sort: SortDescriptor | null) => void
  defaultSort?: SortDescriptor
  /** Stamped onto each row, for the stable hooks E2E selects by. */
  rowAttributes?: (item: T) => Record<string, string | undefined>
  searchPlaceholder?: string
  /** Separates search and table state when the open dialog switches catalogs. */
  catalogKey?: string
  /** Rendered above the table, for a rule the rows can't state on their own. */
  banner?: React.ReactNode
  catalogPanel?: React.ReactNode
  /** Rendered at the footer's leading edge — a "create one" action, typically. */
  createAction?: React.ReactNode
  emptyState?: React.ReactNode
  searchEmptyState?: React.ReactNode
  "data-testid"?: string
}

/**
 * The catalog dialog behind a selection card: search, the entity's own table
 * with a checkbox per row, the running selection as chips, and Apply/Cancel.
 *
 * Every picker in the simulation launcher — agents, personas, test sets —
 * renders this over its own columns, so each one browses the entity the same
 * way its list page presents it.
 */
export function EntityPickerDialog<T>({
  isOpen,
  onCancel,
  onApply,
  applyDisabled = false,
  title,
  items,
  columns,
  getItemId,
  getItemLabel,
  matchesSearch,
  selectedIds,
  onSelectionChange,
  isLoading = false,
  allowMultiple = true,
  isSelectionDisabled,
  reconcileSelection,
  defaultSort,
  onSortChange,
  rowAttributes,
  searchPlaceholder = "Search...",
  catalogKey = "default",
  banner,
  catalogPanel,
  createAction,
  emptyState,
  searchEmptyState,
  "data-testid": testId,
}: EntityPickerDialogProps<T>) {
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({})
  const searchQuery = searchQueries[catalogKey] ?? ""
  const setSearchQuery = (query: string) =>
    setSearchQueries((current) => ({ ...current, [catalogKey]: query }))

  // A query left over from the last visit would silently hide rows the moment
  // the dialog reopens, so every open starts on the full catalog.
  useEffect(() => {
    if (isOpen) {
      setSearchQueries({})
    }
  }, [isOpen])

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) {
      return items
    }
    return items.filter((item) => matchesSearch(item, query))
  }, [items, matchesSearch, searchQuery])

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])

  // Chips read off the selection rather than off the catalog, so an id whose
  // record has gone missing still gets a chip it can be removed from.
  const selectedChips = useMemo(() => {
    const byId = new Map(items.map((item) => [getItemId(item), item] as const))
    return selectedIds.map((id) => {
      const item = byId.get(id)
      return { id, label: item ? getItemLabel(item) : id, isMissing: !item }
    })
  }, [getItemId, getItemLabel, items, selectedIds])

  const commitSelection = useCallback(
    (ids: string[]) => {
      onSelectionChange(reconcileSelection ? reconcileSelection(ids) : ids)
    },
    [onSelectionChange, reconcileSelection],
  )

  const handleSelectionChange = useCallback(
    (nextIds: Set<string>) => {
      if (!allowMultiple) {
        // Single-select: whichever row the change added wins, and the rest of
        // the draft goes with it.
        const added = [...nextIds].find((id) => !selectedIdSet.has(id))
        commitSelection(added ? [added] : [])
        return
      }
      // Kept in selection order, with newly checked rows appended, so the chip
      // row and the summary card don't reshuffle on every toggle.
      const retained = selectedIds.filter((id) => nextIds.has(id))
      const added = [...nextIds].filter((id) => !selectedIdSet.has(id))
      commitSelection([...retained, ...added])
    },
    [allowMultiple, commitSelection, selectedIdSet, selectedIds],
  )

  const handleRowClick = useCallback(
    (item: T) => {
      const id = getItemId(item)
      if (selectedIdSet.has(id)) {
        commitSelection(selectedIds.filter((selected) => selected !== id))
        return
      }
      if (isSelectionDisabled?.(item)) {
        return
      }
      commitSelection(allowMultiple ? [...selectedIds, id] : [id])
    },
    [
      allowMultiple,
      commitSelection,
      getItemId,
      isSelectionDisabled,
      selectedIdSet,
      selectedIds,
    ],
  )

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent
        className={cn("w-full max-w-6xl", catalogPanel && "overflow-hidden")}
        data-testid={testId}
      >
        {/* Title and search share the header row: the search filters the catalog
            below rather than heading it, so a full-width field under the title
            read as a second heading and pushed the rows down a band. */}
        <DialogHeader
          className={cn(
            // Symmetric vertical padding: the title sits the same distance off
            // the dialog's top edge as off whatever closes the header below it.
            "flex-row items-center justify-between gap-4 border-b px-6 py-5",
            // Keep the border space when chips carry the visible rule so selection changes do not resize the dialog.
            selectedChips.length === 0
              ? "border-coval-border"
              : "border-transparent",
          )}
        >
          <DialogTitle className="text-xl">
            {selectedIds.length > 0
              ? `${title} (${selectedIds.length})`
              : title}
          </DialogTitle>
          <SearchInput
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onClear={() => setSearchQuery("")}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            data-testid={testId ? `${testId}-search` : undefined}
            className="w-72 shrink-0"
          />
        </DialogHeader>
        {/* The table owns the body's horizontal gutter, so the body adds none. */}
        <DialogBody className="overflow-hidden p-0">
          <div className="flex h-[70vh] min-h-0 w-full flex-col">
            {selectedChips.length > 0 ? (
              <div className="flex max-h-24 flex-wrap items-center gap-2 overflow-y-auto border-b border-coval-border px-6 pb-3">
                {selectedChips.map(({ id, label, isMissing }) => (
                  <SelectionChip
                    key={id}
                    label={label}
                    invalid={isMissing}
                    onRemove={() =>
                      commitSelection(
                        selectedIds.filter((selected) => selected !== id),
                      )
                    }
                  />
                ))}
              </div>
            ) : null}

            {banner ? (
              <div className="bg-surface-contrast px-6 py-3">{banner}</div>
            ) : null}

            <div className="min-h-0 flex-1">
              <Table<T>
                key={catalogKey}
                aria-label={title}
                data={filteredItems}
                columns={columns}
                getRowId={getItemId}
                isLoading={isLoading}
                selectable
                selectionVisibility="always"
                selectedIds={selectedIdSet}
                onSelectionChange={handleSelectionChange}
                // Single-select has nothing to select all of, so it gets row
                // checkboxes only — and no shift-click range, which could only
                // hand back rows the single choice would have to throw away.
                showSelectAll={allowMultiple}
                allowRangeSelection={allowMultiple}
                isRowSelectionDisabled={
                  isSelectionDisabled
                    ? (item) =>
                        !selectedIdSet.has(getItemId(item)) &&
                        isSelectionDisabled(item)
                    : undefined
                }
                // The row click adds or removes the item, so the checkbox cell
                // stays part of the target instead of opting out.
                rowClickSelects
                onRowClick={handleRowClick}
                // No hover tint: every row here is a candidate, and the picked
                // ones are what the surface is for. The checkboxes are on show
                // from the start (`selectionVisibility`), so a tint would only
                // compete with the selected rows for the eye.
                rowHover={false}
                defaultSort={defaultSort}
                onSortChange={onSortChange}
                rowAttributes={rowAttributes}
                emptyState={
                  searchQuery
                    ? (searchEmptyState ?? "No results match your search")
                    : (emptyState ?? "Nothing to show")
                }
                // Platter rows: the catalog reads as one bordered card on the
                // default surface, the same treatment the metric picker gives
                // its sections, with the contrast surface around it.
                cardRows
                // The scroll container carries the px-6 gutter that lines the
                // platter up with the dialog's header and search field, so
                // collapse the Table's own 12px edge gutter to zero — otherwise
                // the platter would sit px-6 + 12px in from the edge. Platter
                // rows keep their cell padding regardless, so the first column
                // stays inset from the platter's border.
                edgeToEdge
                className="h-full bg-surface-contrast px-6 pb-6"
              />
            </div>
          </div>
        </DialogBody>
        <DialogFooter className="border-t border-coval-border px-6 py-5">
          {createAction ? <div className="mr-auto">{createAction}</div> : null}
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={onApply}
            disabled={applyDisabled}
            data-testid={testId ? `${testId}-apply-button` : undefined}
          >
            Apply Selection
          </Button>
        </DialogFooter>
        {catalogPanel}
      </DialogContent>
    </Dialog>
  )
}
